import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  VENDOR_FIELDS,
  VENDOR_LABELS,
  type VendorKey,
  vendorPatchBody,
  vendorToggleBody,
} from '../../../features/admin/vendors'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { ApiError } from '../../../lib/api-client'
import { Btn, Field, Inline, input, Tag } from './controls'
import { Header, mono, Note, SkeletonRows, serif } from './ui'

/** Error text for admin mutations; a 409 gets an explicit conflict prefix. */
export function adminError(e: unknown): string {
  const msg = apiErrorMessage(e)
  return e instanceof ApiError && e.status === 409 ? `Conflict: ${msg}` : msg
}

export const td = 'border-t border-[var(--color-border)] py-3 pr-4 align-baseline text-[14.5px]'

/** Ledger page frame: eyebrow, serif title, standfirst, and loading/error states. */
export function AdminPage({
  title,
  standfirst,
  aside,
  loading,
  error,
  children,
}: {
  title: string
  standfirst: ReactNode
  aside?: ReactNode
  loading?: boolean
  error?: boolean
  children: ReactNode
}) {
  return (
    <div>
      <Header eyebrow="Administration" title={title} standfirst={standfirst} aside={aside} />
      {loading ? (
        <SkeletonRows />
      ) : error ? (
        <Note tone="error">We couldn't load this page. You may not have access, or try again in a moment.</Note>
      ) : (
        children
      )}
    </div>
  )
}

/** A masked secret with Reveal; the revealed value auto-masks after 30s and lives only in component state. */
function SecretValue({ masked, reveal }: { masked: string; reveal: () => Promise<string> }) {
  const [value, setValue] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )
  const hide = () => {
    if (timer.current) clearTimeout(timer.current)
    setValue(null)
  }
  const show = async () => {
    setBusy(true)
    setErr(null)
    try {
      setValue(await reveal())
      timer.current = setTimeout(() => setValue(null), 30_000)
    } catch (e) {
      setErr(adminError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div>
      <div className="flex items-baseline gap-3">
        <code className="min-w-0 flex-1 truncate text-[13px]" style={mono}>
          {value ?? (masked || 'Not set')}
        </code>
        {masked ? (
          value ? (
            <Btn onClick={hide}>Hide</Btn>
          ) : (
            <Btn onClick={show} disabled={busy}>
              {busy ? 'Revealing…' : 'Reveal'}
            </Btn>
          )
        ) : null}
      </div>
      {value ? <p className="text-[12px] italic text-[var(--color-muted)]">Hides again in 30 seconds.</p> : null}
      {err ? <Inline tone="bad">{err}</Inline> : null}
    </div>
  )
}

/** One vendor as a ledger entry: facts, edit (blank secret = keep), enable toggle, reveal. */
export function VendorEntry({
  vendor,
  block,
  pending,
  error,
  onSave,
  reveal,
}: {
  vendor: VendorKey
  block: Record<string, unknown> | null
  pending: boolean
  error: unknown
  onSave: (body: { vendor: VendorKey; data: Record<string, unknown> }) => void
  reveal: (vendor: VendorKey, field: string) => Promise<string>
}) {
  const [edits, setEdits] = useState<Record<string, string>>({})
  const f = VENDOR_FIELDS[vendor]
  const enabled = block?.enabled === true
  const str = (k: string) => (typeof block?.[k] === 'string' ? (block[k] as string) : '')
  const patch = vendorPatchBody(vendor, edits)
  const dirty = Object.keys(patch.data).length > 0
  return (
    <section className="mt-10">
      <div className="flex items-baseline justify-between gap-4 border-b border-[var(--v3-rule-strong)] pb-2">
        <h2 className="flex items-baseline gap-3 text-[20px] font-semibold tracking-[-0.01em]" style={serif}>
          {VENDOR_LABELS[vendor]}
          <Tag tone={enabled ? 'ok' : 'off'}>{enabled ? 'Enabled' : block ? 'Disabled' : 'Not configured'}</Tag>
        </h2>
        <Btn disabled={pending} onClick={() => onSave(vendorToggleBody(vendor, !enabled))}>
          {enabled ? 'Disable' : 'Enable'}
        </Btn>
      </div>
      <div className="grid gap-x-10 gap-y-5 pt-5 sm:grid-cols-2">
        {f.nonSecret.map((k) => (
          <Field key={k} label={k}>
            <input
              className={input}
              style={mono}
              defaultValue={str(k)}
              onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))}
            />
          </Field>
        ))}
        {f.secret.map((k) => (
          <div key={k}>
            <span className="mb-1 block text-[12px] text-[var(--color-muted)]">{k}</span>
            <SecretValue masked={str(k)} reveal={() => reveal(vendor, k)} />
            <input
              aria-label={`New ${k}`}
              className={`${input} mt-2`}
              type="password"
              autoComplete="off"
              placeholder="Leave blank to keep current"
              onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center justify-end gap-5">
        {error ? <Inline tone="bad">{adminError(error)}</Inline> : null}
        <Btn variant="primary" disabled={!dirty || pending} onClick={() => onSave(patch)}>
          {pending ? 'Saving…' : 'Save'}
        </Btn>
      </div>
    </section>
  )
}
